import * as React from 'react';
import { useQuery } from 'react-apollo';
import gql from 'graphql-tag';
import { GetGroupBy, GetGroupByPlurals, formatNumber, GetArrow, getMaxSuggestedTick, getHighestNumberInArray, convertStringNumberToNumber } from '../../helpers/ChartHelper';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { ChartCommonProps, IconButtonWrapper, ChartCommonState } from '../Common';
import { IconButton, makeStyles, Theme, createStyles } from '@material-ui/core';
import { ChartFilter } from '../ChartComponents';
import { DrillDownType } from '../DrillDownType';
import { SelectedItemProps } from '../MattersByStatusChart';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import { useScreenshot, createFileName } from 'use-react-screenshot';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import { useEffect } from 'react';
import MTooltip from '@material-ui/core/Tooltip';
import { ChartInfo } from '../../components/ChartInfo';

import { RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvLegendLabels, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { ChartLimitNote } from '../ChartLimitNote';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { ChartEmptyState } from '../ChartEmptyState';
import { ChartNotes } from '../ChartNotes';
import ReceiptOutlinedIcon from '@material-ui/icons/ReceiptOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            // minHeight: '100%',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            [`& ${`span`}`]: {
                // marginBottom: '15px',
                // display: 'block',
                // textAlign: 'center',
                // with: '100%',
            },
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
            },
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            position: 'relative',
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        titleBarContainer: {
            display: 'flex',
            flexFlow: 'column',
            alignItems: 'center',
            padding: '15px 15px 0px 15px',
            fontSize: '16px',
            fontWeight: 600,
            [`& .${`h1`}`]: {
                display: 'flex',
                alignItems: 'center',
            },
            [`& .${`h2`}`]: {
                fontSize: '2.5rem',
                padding: '10px 0',
            },
            [`& .${`h3`}`]: {
                paddingTop: '10px',
            },
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            // height: '100vh',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media print': {
                height: '300px',
                width: '300px',
            },
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            [`& .${`titleWrapper`}`]: {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                [`& .${`title`}`]: {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                [`& .${`value`}`]: {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                [`& .${`secondary-title`}`]: {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
            '&:hover': {
                '& .scroll': {
                    display: 'flex !important',
                    '& .Mui-disabled': {
                        '& .MuiSvgIcon-root': {
                            color: 'inherit !important'
                        }
                    }
                }
            }
        },
        footerDescription: {
            fontSize: '1rem',
            textAlign: 'center',
        },
        scrollUp: {
            display: 'none',
            position: 'absolute',
            top: '30px',
            justifyContent: 'center',
            transform: 'translateX(-50%)',
            left: '50%',
            minWidth: '54px',
        },
        scrollDown: {
            display: 'none',
            position: 'absolute',
            bottom: '90px',
            justifyContent: 'center',
            transform: 'translateX(-50%)',
            left: '50%',
            minWidth: '54px',
        }
    }),
);

interface ChartProps extends SelectedItemProps, ChartCommonProps {
    groupBy: string;
    chartFilter?: ChartFilter;
}

interface ChartState extends ChartCommonState {
    symbol: string;
    groupedByCount: number;
    previousYearTotalCount: number;
    lastYearTotalCount: number;
    groupedBy: string[];
    lastYearCount: number[];
    previousYearCount: number[];
}

export const InvoiceFeesBilledInLastYearGroupedChart: React.FC<ChartProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const entireChartRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const scrollUpAndPrevRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const scrollDownAndNextRef = React.useRef<any>();

    const invoiceFeesBilledInLastYearQuery = useQuery<InvoiceFeesBilledInLastYearData>(INVOICE_FEES_BILLED_IN_LAST_YEAR_QYERY, {
        variables: {
            groupBy: props.groupBy,
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility,
            showAllRecords: props.showAllRecords,
        },
    });

    const currencySymbolQuery = useQuery<CurrencySymbolData>(CurrencySymbol);

    const takeScreenShot = useScreenshot({
        type: 'image/jpeg',
        quality: 1.0,
    })[1];

    const [state, setState] = React.useState<ChartState>({
        open: false,
        anchorEl: undefined,
        symbol: '$',
        groupedBy: [],
        lastYearCount: [],
        previousYearCount: [],
        groupedByCount: 0,
        previousYearTotalCount: 0,
        lastYearTotalCount: 0,
    });

    useEffect(() => {
        window.addEventListener('keydown', onKeyDownHandler);

        return () => {
            window.removeEventListener('keydown', onKeyDownHandler);
        };
        // tslint:disable-next-line: align
    }, []);

    // tslint:disable-next-line: no-any
    const onKeyDownHandler = (event: any) => {
        if (event.keyCode === 80 && (event.ctrlKey || event.metaKey) && !event.altKey) {
            event.preventDefault();
            if (event.stopImmediatePropagation) {
                event.stopImmediatePropagation();
            } else {
                event.stopPropagation();
            }
            return;
        }
    };
    
    useEffect(() => {
        if (!currencySymbolQuery.loading && !currencySymbolQuery.error && currencySymbolQuery.data) {
            setState((prevState) => {
                const symbol = currencySymbolQuery.data!.options.currencySymbol.symbol;

                return {
                    ...prevState,
                    symbol: symbol,
                };
            });
        }
        // tslint:disable-next-line: align
    }, [currencySymbolQuery.loading, currencySymbolQuery.data, currencySymbolQuery.error]);

    useEffect(() => {
        if (
            !invoiceFeesBilledInLastYearQuery.loading &&
            !invoiceFeesBilledInLastYearQuery.error &&
            invoiceFeesBilledInLastYearQuery.data &&
            invoiceFeesBilledInLastYearQuery.data.statistics &&
            invoiceFeesBilledInLastYearQuery.data.statistics.invoice &&
            invoiceFeesBilledInLastYearQuery.data.statistics.invoice.invoiceFeesBilledInLastYear
        ) {
            setState((prevState) => {
                const { groupedByCount, groupedInvoiceForLastYear, invoiceYearTotalCount } = invoiceFeesBilledInLastYearQuery.data!.statistics.invoice.invoiceFeesBilledInLastYear;

                let previousYearTotalCount: number = invoiceYearTotalCount.previousYearTotalCount;
                let lastYearTotalCount: number = invoiceYearTotalCount.lastYearTotalCount;

                let groupedBy: string[] = groupedInvoiceForLastYear.map((group: GroupedInvoiceForLastYear) => group.groupedBy);
                let lastYearCount: number[] = groupedInvoiceForLastYear.map((group: GroupedInvoiceForLastYear) => group.lastYearCount);
                let previousYearCount: number[] = groupedInvoiceForLastYear.map((group: GroupedInvoiceForLastYear) => group.previousYearCount);

                return {
                    ...prevState,
                    groupedByCount: groupedByCount,
                    previousYearTotalCount: previousYearTotalCount,
                    lastYearTotalCount: lastYearTotalCount,
                    groupedBy: groupedBy,
                    lastYearCount: lastYearCount,
                    previousYearCount: previousYearCount,
                };
            });
        }
        // tslint:disable-next-line: align
    }, [invoiceFeesBilledInLastYearQuery.loading, invoiceFeesBilledInLastYearQuery.data, invoiceFeesBilledInLastYearQuery.error]);

    const handleTotalClick = (isCurrent: Boolean) => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();

            searchCriterias.push(
                { property: 'Status', operator: 'in', comparison: 'Billed' },
                { property: '12MonthPeriod', operator: 'eq', comparison: isCurrent ? 'curr' : 'prev' },
            );

            props.getSelectedItem(searchCriterias, false, `Fees Billed In the Last Year by ${GetGroupBy(props.groupBy)}`, DrillDownType.invoiceSummary, 'Invoices');
        }
    };

    const onCloseChart = () => {
        if (props.onCloseChart) {
            return props.onCloseChart(`isFeesBilledInLastYearBy${props.groupBy}Open`);
        }
    };

    const onScrollUpAndPrev = (type: 'up' | 'prev') => {
        if (chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min > 0) {
            const scrollRatio = chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].max - chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min;
            const min = chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min;
            if (scrollRatio < chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min) {
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min -= scrollRatio ? scrollRatio : 1;
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].max -= scrollRatio ? scrollRatio : 1; 
            } else {
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min -= min;
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].max -= min;
            }
            chartRef.current.options.scales[type === 'up' ? 'x' : 'y'].min = 0;
            chartRef.current.update();
            // enabled scroll up
            scrollUpAndPrevRef.current.classList.remove('Mui-disabled');
        } else {
            // disabled scroll up
            scrollUpAndPrevRef.current.classList.add('Mui-disabled');
        }
        scrollDownAndNextRef.current.classList.remove('Mui-disabled');
    };
    
    const onScrollDownAndNext = (type: 'down' | 'next') => {

        const labels = state.groupedBy;

        if (chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max < labels.length) {
            const remainingBar = labels.length - chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max;
            const scrollRatio = chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max - chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].min;
            if (remainingBar <= scrollRatio) {
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].min += remainingBar;
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max += remainingBar;
            } else {
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].min += scrollRatio ? scrollRatio : 1;
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max += scrollRatio ? scrollRatio : 1;
            }
            chartRef.current.options.scales[type === 'down' ? 'x' : 'y'].min = 0;
            chartRef.current.update();
            // enabled scroll down
            scrollDownAndNextRef.current.classList.remove('Mui-disabled');
        } else {
            // disabled scroll down
            scrollDownAndNextRef.current.classList.add('Mui-disabled');
        }
        scrollUpAndPrevRef.current.classList.remove('Mui-disabled');
    };

    const onScollBar = (type: 'up' | 'down' | 'prev' | 'next') => () => {
        if (type === 'up') {
            onScrollUpAndPrev('up');
        } else if (type === 'down') {
            onScrollDownAndNext('down');
        }  else if (type === 'next') {
            onScrollDownAndNext('next');
        } else {
            onScrollUpAndPrev('prev');
        } 
    };

    const onDisabledEnabledArrow = (barType: 'horizontal' | 'vertical') => {  
        const labels = state.groupedBy;
        // up 
        if (chartRef.current.options.scales[barType === 'horizontal' ? 'y' : 'x'].min > 0) {
            scrollUpAndPrevRef.current.classList.remove('Mui-disabled');
        } else {
            scrollUpAndPrevRef.current.classList.add('Mui-disabled');
        }

        // down
        if (chartRef.current.options.scales[barType === 'horizontal' ? 'y' : 'x'].max < labels.length) {
            scrollDownAndNextRef.current.classList.remove('Mui-disabled');
        } else {
            scrollDownAndNextRef.current.classList.add('Mui-disabled');
        }
    };

    const onPan = (context: { chart: Chart }) => {

        onDisabledEnabledArrow('horizontal');

        chartRef.current.options.scales.x.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };

    const onZoom = (context: { chart: Chart }) => {

        onDisabledEnabledArrow('horizontal');

        chartRef.current.options.scales.x.min = 0;
        chartRef.current.update();
    };

    const onMaximize = () => {
        if (props.onMaximize) {
            props.onMaximize();
        }
    };

    // tslint:disable-next-line: no-any
    const onContextMenu = (event: any) => {
        event.preventDefault();
        if (event.button !== 2) {
            return false;
        }
        return true;
    };

    // tslint:disable-next-line: no-any
    const onDownload = (name: string, extension: string = 'jpg') => (image: any) => {
        const a = document.createElement('a');
        a.href = image;
        a.download = createFileName(extension, name);
        a.click();
    };

    // tslint:disable-next-line: no-any
    const onDownloadScreenshot = (event: any) => {
        event.preventDefault();
        event.stopPropagation();
        setTimeout(() => {
            takeScreenShot(entireChartRef.current).then(onDownload(`Fees Billed In the Last Year by ${GetGroupBy(props.groupBy)}`));
        // tslint:disable-next-line: align
        }, 500);
    };

    const chartControlComponent = () => {
        return (
            <>
                <IconButtonWrapper aria-label="close">
                    <ChartInfo 
                        description={props.description}
                        fontSize={props.showAllRecords ? 'large' : 'medium'}
                        className={`${classes.helpIcon} rv-chart-help`}
                    />
                    {props.showAllRecords && (
                        <IconButton size="medium" onClick={onDownloadScreenshot}>
                            <GetAppOutlinedIcon color="primary" fontSize="large" />
                        </IconButton>
                    )}
                    {!props.showAllRecords && props.onMaximize && (
                        <MTooltip title="Expand chart">
                            <IconButton aria-label="maximize" onClick={onMaximize} size="small">
                                <AspectRatioIcon />
                            </IconButton>
                        </MTooltip>
                    )}
                    <IconButton aria-label="close" onClick={onCloseChart} size={props.showAllRecords ? 'medium' : 'small'}>
                        <CloseIcon fontSize={props.showAllRecords ? 'large' : 'medium'} />
                    </IconButton>
                </IconButtonWrapper>
            </>
        );
    };

    if (invoiceFeesBilledInLastYearQuery.loading || currencySymbolQuery.loading) {
        return (
            <div className="rv-chart rv-chart-loading">
                {chartControlComponent()}
                <ChartSkeleton variant="bars" />
            </div>
        );
    }

    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.previousYearCount, state.lastYearCount) ? ' rv-chart--empty' : ''}`} ref={entireChartRef}>
            {chartControlComponent()}
            <div className={classes.titleBarContainer}>
                <ChartHeading icon={<ReceiptOutlinedIcon />} title="Fees Billed In the Last Year" />
                <div className={`${classes.totalDiv} h2`} onClick={() => handleTotalClick(true)}>
                    <AnimatedNumber value={state.lastYearTotalCount} format={(amount) => state.symbol + formatNumber(amount)} />
                    {GetArrow(state.lastYearTotalCount, state.previousYearTotalCount)}
                </div>
                <div className="h3">
                    <div className={classes.totalDiv} onClick={() => handleTotalClick(false)}>
                        {state.symbol + formatNumber(state.previousYearTotalCount)}
                    </div>
                    in the year previous to that
                </div>
                <div className="h3 rv-chart-section">Fees Billed by {GetGroupBy(props.groupBy)}</div>
            </div>
            <div className={classes.barContainer} style={{ maxHeight: props.showAllRecords ? '100%' : '350px' }}>
                {props.showAllRecords && (
                    <>
                        <div className={`${classes.scrollUp} scroll`}>
                            <IconButton 
                                size="medium" 
                                onClick={onScollBar('up')}
                                ref={scrollUpAndPrevRef}
                            >
                                <KeyboardArrowUpIcon color="primary" fontSize="large" />
                            </IconButton>
                        </div>
                        <div className={`${classes.scrollDown} scroll`}>
                            <IconButton 
                                size="medium" 
                                onClick={onScollBar('down')}
                                ref={scrollDownAndNextRef}
                            >
                                <KeyboardArrowDownIcon color="primary" fontSize="large" />
                            </IconButton>
                        </div>
                    </>
                )}
                <Bar
                    ref={chartRef}
                    onContextMenu={onContextMenu}
                    // tslint:disable-next-line: no-any
                    onClick={(event: React.MouseEvent<any>) => {
                        const _data = getElementAtEvent(chartRef.current, event);
                        if (_data.length > 0 && props.getSelectedItem) {
                            const _index = getElementAtEvent(chartRef.current, event)[0].index;
                            const _datasetIndex = getElementAtEvent(chartRef.current, event)[0].datasetIndex;

                            const item = state.groupedBy[_index];

                            const searchCriterias = new Array<SearchCriteria>();

                            searchCriterias.push(
                                { property: props.groupBy, operator: 'eq', comparison: item },
                                { property: 'Status', operator: 'in', comparison: 'Billed' },
                                { property: '12MonthPeriod', operator: 'eq', comparison: _datasetIndex === 1 ? 'curr' : 'prev' },
                            );

                            props.getSelectedItem(searchCriterias, false, `Fees Billed In the Last Year by ${GetGroupBy(props.groupBy)}`, DrillDownType.invoiceSummary, 'Invoices');
                        }
                    }}
                    data={{
                        labels: state.groupedBy,
                        datasets: [
                            {
                                label: 'Previous 12 months',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primarySoft),
                                data: state.previousYearCount,
                            },
                            {
                                label: 'Current 12 months',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                                data: state.lastYearCount,
                            },
                        ],
                    }}
                    redraw={true}
                    options={{
                        onHover: onHover,
                        indexAxis: 'y' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            x: {
                                grid: rvValueAxisStyle.grid,
                                // beginAtZero: true,
                                suggestedMax: getMaxSuggestedTick(
                                    getHighestNumberInArray([
                                        ...state.previousYearCount,
                                        ...state.lastYearCount
                                    ]),
                                    6,
                                ),
                                beginAtZero: true,
                                ticks: {
                                    ...rvValueAxisStyle.ticks,
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${state.symbol}${formatNumber(tickValue)}`;
                                    },
                                },
                            },
                            y: {
                                grid: rvCategoryAxisStyle.grid,
                                min: 0,
                                max: props.showAllRecords ? 29 : 9,
                                ticks: {
                                    ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false
                                }
                            }
                        },
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: true,
                                labels: rvLegendLabels,
                            },
                            title: {
                                display: false,
                                text: ``,
                            },
                            zoom: {
                                pan: {
                                    enabled: props.showAllRecords,
                                    onPan: onPan,
                                },
                                zoom: {
                                    wheel: {
                                        enabled: props.showAllRecords,
                                    },
                                    onZoom: onZoom,
                                },
                                // limits: {
                                //     y: { min: 0, max: 9 }
                                // }
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: function (context: any) {
                                        const {
                                            //   dataIndex,
                                            //   label,
                                            formattedValue,
                                            //   dataset
                                        } = context;

                                        var currentValue = convertStringNumberToNumber(formattedValue);

                                        return `Billed Fees ${state.symbol}${formatNumber(currentValue)}`;
                                    },
                                },
                            },
                        },
                    }}
                />
                {isChartDataEmpty(state.previousYearCount, state.lastYearCount) && (
                    <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                )}
            </div>
            {!props.showAllRecords && state.groupedByCount > 10 && <ChartLimitNote>The top 10 {GetGroupByPlurals(props.groupBy)} are shown</ChartLimitNote>}
            <ChartNotes notes={['Information collated based on matter responsibility']} actionHint="Drill down to invoice details" />
        </div>
    );
};

export default InvoiceFeesBilledInLastYearGroupedChart;

export interface CurrencySymbolData {
    options: Options;
}

export interface Options {
    currencySymbol: CurrencySymbol;
}

export interface CurrencySymbol {
    symbol: string;
}

const CurrencySymbol = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;

export interface InvoiceFeesBilledInLastYearData {
    statistics: Statistics;
}

export interface Statistics {
    invoice: Invoice;
}

export interface Invoice {
    invoiceFeesBilledInLastYear: InvoiceFeesBilledInLastYear;
}

export interface InvoiceFeesBilledInLastYear {
    groupedInvoiceForLastYear: GroupedInvoiceForLastYear[];
    groupedByCount: number;
    invoiceYearTotalCount: InvoiceYearTotalCount;
}

export interface GroupedInvoiceForLastYear {
    groupedBy: string;
    lastYearCount: number;
    previousYearCount: number;
}

export interface InvoiceYearTotalCount {
    previousYearTotalCount: number;
    lastYearTotalCount: number;
}

const INVOICE_FEES_BILLED_IN_LAST_YEAR_QYERY = gql`
    query InvoiceFeesBilledInLastYearData($groupBy: InvoiceGroupBy, $entityType: Int, $entityGuid: String, $responsibility: Int, $showAllRecords: Boolean) {
        statistics {
            invoice {
                invoiceFeesBilledInLastYear(groupBy: $groupBy, entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility, showAllRecords: $showAllRecords) {
                    groupedInvoiceForLastYear {
                        groupedBy
                        lastYearCount
                        previousYearCount
                    }
                    groupedByCount
                    invoiceYearTotalCount {
                        previousYearTotalCount
                        lastYearTotalCount
                    }
                }
            }
        }
    }
`;
